import {
  chunk,
  classNames,
  MemberPageGridTypeData,
  PageGridTypeData,
  PanuiGridTypeData,
  Page,
} from "@plinknz/tah-website-elements";
import { format, parse } from "date-fns";
import * as React from "react";
import { Link } from "react-router-dom";
import { generate } from "shortid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { byPrefixAndName } from "@awesome.me/kit-d560caf08c/icons/modules";
import { Accordion } from "../accordion";

export interface NewsGridProps {
  data: MemberPageGridTypeData | PageGridTypeData | PanuiGridTypeData;
  children: Page[];
}

const NewsGridItem = ({
  title,
  slug,
  cover_image,
  thumbnail_image,
  published_at,
}: Page) => {
  const image = thumbnail_image || cover_image[0];
  const item = React.useRef<HTMLAnchorElement>(null);
  const imageUrl = image?.url;
  const hasThumbnailImage = !!imageUrl;
  const itemClass = classNames("news-grid-item", {
    "has-background": hasThumbnailImage,
  });

  return (
    <Link
      style={{
        backgroundImage: `url("${imageUrl || "/images/defaults/image1.jpg"}")`,
      }}
      key={generate()}
      className={itemClass}
      ref={item}
      to={slug}
    >
      <h3 className="news-grid-item-title">
        {title}
        {hasThumbnailImage}{" "}
        <FontAwesomeIcon icon={byPrefixAndName.fas["chevron-right"]} />
      </h3>
      {published_at && (
        <p className="news-grid-item-date">
          <time>{format(new Date(published_at), "do 'of' MMMM")}</time>
        </p>
      )}
    </Link>
  );
};

interface Month {
  date: string;
  pages: Page[];
}

const SHOW_BY_DATE_THRESHOLD = 15;

export const NewsGridBlock = ({ data, children }: NewsGridProps) => {
  let pageData = data;
  const dateKeyFormat = "M-d-yyyy";
  const referenceDate = new Date();
  // eslint-disable-next-line no-underscore-dangle

  if ("use_children" in data && data.use_children) {
    pageData = {
      ...pageData,
      pages: children,
    };
  }

  const sortable = pageData.pages.every(({ published_at }) => !!published_at);

  const splitByDate =
    sortable && pageData.pages.length > SHOW_BY_DATE_THRESHOLD;

  const renderRow = (pages: Page[], index: number, colStyle: string) => (
    <div key={index} className={`news-grid-items ${colStyle}`}>
      {pages.map(NewsGridItem)}
    </div>
  );

  const gridifyRender = (pages: Page[]) => {
    const pageCount = pages.length;
    let cols = 3;
    let colStyle = "";

    if (pageCount === 1) {
      cols = 1;
      colStyle = "news-grid-items-one-col";
    } else if (pageCount === 2 || pageCount === 4) {
      cols = 2;
      colStyle = "news-grid-items-two-col";
    }
    const grid = chunk<Page>(pages, cols);
    return grid.map((item, index) => renderRow(item, index, colStyle));
  };

  const renderByDate = (pages: Page[]) => {
    const months: Month[] = [];
    pages.forEach((page) => {
      const published = new Date(page.published_at).setDate(1); // set all to first of the month
      const key = format(published, dateKeyFormat);
      const currentMonthIndex = months.findIndex(({ date }) => date === key);

      if (currentMonthIndex > -1) {
        months[currentMonthIndex].pages.push(page);
      } else {
        months.push({ date: key, pages: [page] });
      }
    });

    const sortedMonths = months.sort(
      (a, b) => new Date(b.date).valueOf() - new Date(a.date).valueOf()
    );

    return (
      <>
        {sortedMonths.map(({ date, pages: monthsPages }, index) => (
          <Accordion
            header={format(
              parse(date, dateKeyFormat, referenceDate),
              "MMMM, yyyy"
            )}
            expand={index === 0}
            itemCount={monthsPages.length}
            key={date}
          >
            {gridifyRender(monthsPages)}
          </Accordion>
        ))}
      </>
    );
  };

  return (
    <div className="news-grid" data-testid="news-grid">
      <div className="news-grid-wrapper || constrain-width">
        <h3>{pageData.title}</h3>
        {(splitByDate && <>{renderByDate(pageData.pages)}</>) ||
          gridifyRender(pageData.pages)}
      </div>
    </div>
  );
};
